import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';

import {
  SuiDialogConfig,
  DialogCloseReason,
  DialogService,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import { SuiDialogBase } from '@sui/dialog/sui-dialog-base';
import { ComboboxOption, SuiComboboxComponent } from '@sui/combobox';

import {
  injectExecuteUserCompanyMoveMutation,
  injectPlanUserCompanyMoveMutation,
  UserCompanyMoveOutcome,
} from 'src/app/api/queries/user.queries';
import {
  UserApiService,
  UserCompanyMoveReport,
  UserCompanyMoveRunStatus,
} from 'src/app/api/services/user-api.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { clearSelectedPrincipals } from 'src/app/core/state/principals.actions';
import { PickerDataobjectsDialogComponent } from 'src/app/objects/dialogs/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import { NomObjectClass, PopupType } from 'src/app/core/constants';
import { ObjectDefinition, User } from 'src/app/core/models/core.models';
import { Principal } from 'src/app/core/models/object.models';
import { UserService } from 'src/app/core/services/user.service';
import { AppState } from 'src/app/core/state/app.state';
import { SuiButtonComponent } from '@sui/button';
import {
  SuiDetailItemComponent,
  SuiDetailListComponent,
} from '@sui/detail-list';
import { NgpFormField, NgpLabel } from 'ng-primitives/form-field';
import { SuiFormFieldComponent, SuiLabelDirective } from '@sui/form-field';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export interface MoveUserCompanyDialogData {
  user: Pick<
    User,
    'id' | 'display_name' | 'email' | 'company_id' | 'company_name'
  >;
}

/** Closed with reason Save once the run executed: the report plus the re-read user. */
export type MoveUserCompanyDialogOutput = UserCompanyMoveOutcome;

/** One non-zero row of the report's owned-object counts. */
interface OwnedObjectsRow {
  key: string;
  labelKey: string;
  active: number;
  archived: number;
  deleted: number;
}

/**
 * Move a user to another company (WAP-1702), for a Global Company Manager from
 * the user editor: pick the target company, plan (the server answers with what
 * the move touches), optionally choose the new owner of the objects left in the
 * source company, then execute. A planned run that is not executed is cancelled
 * on close.
 */
import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';
@Component({
  selector: 'app-move-user-company-dialog',
  templateUrl: './move-user-company-dialog.component.html',
  styleUrl: './move-user-company-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDetailListComponent,
    SuiDetailItemComponent,
    SuiDialogContentSlot,
    NgpFormField,
    SuiFormFieldComponent,
    NgpLabel,
    SuiLabelDirective,
    SuiComboboxComponent,
    FormsModule,
    ReactiveFormsModule,
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class MoveUserCompanyDialogComponent
  extends SuiDialogBase<MoveUserCompanyDialogData, MoveUserCompanyDialogOutput>
  implements OnInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { width: 560 };
  private readonly presenter = inject(ApiErrorPresenterService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);
  private readonly userApi = inject(UserApiService);
  private readonly utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly destroyRef = inject(DestroyRef);

  readonly planMutation = injectPlanUserCompanyMoveMutation();
  readonly executeMutation = injectExecuteUserCompanyMoveMutation();

  readonly targetCompanyControl = new FormControl<string | null>(null);

  readonly companyOptions = signal<ComboboxOption<string>[]>([]);
  readonly companiesLoading = signal(true);
  readonly report = signal<UserCompanyMoveReport | null>(null);
  readonly newOwner = signal<Principal | null>(null);
  readonly outcome = signal<UserCompanyMoveOutcome | null>(null);

  private readonly targetCompanyId = toSignal(
    this.targetCompanyControl.valueChanges,
    { initialValue: this.targetCompanyControl.value },
  );

  readonly canPlan = computed(
    () => !!this.targetCompanyId() && !this.planMutation.isPending(),
  );

  readonly canMove = computed(() => {
    const report = this.report();
    return (
      !!report &&
      (!report.requires_new_owner || !!this.newOwner()) &&
      !this.executeMutation.isPending()
    );
  });

  readonly ownedObjectRows = computed<OwnedObjectsRow[]>(() => {
    const counts = this.report()?.counts ?? {};
    return Object.entries(counts)
      .filter(([, c]) => c.active + c.archived + c.deleted > 0)
      .map(([key, c]) => ({
        key,
        labelKey: key.replace(/_/g, '-'),
        active: c.active,
        archived: c.archived,
        deleted: c.deleted,
      }));
  });

  readonly groupsLeft = computed(
    () => this.report()?.warnings.groups.join(', ') ?? '',
  );

  readonly executed = computed(
    () =>
      this.outcome()?.report.run_status_id ===
      UserCompanyMoveRunStatus.Executed,
  );

  get user(): MoveUserCompanyDialogData['user'] {
    return this.dialogData.user;
  }

  ngOnInit(): void {
    this.userService
      .fetchUserCompanies('', '', true, true, true)
      .pipe(
        catchError(() => of([])),
        tap((companies) => {
          this.companyOptions.set(
            companies
              .filter((company) => String(company.id) !== this.user.company_id)
              .map((company) => ({
                label: company.name ?? company.title,
                value: String(company.id),
              })),
          );
          this.companiesLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  plan(): void {
    const targetCompanyId = this.targetCompanyId();
    if (!targetCompanyId) {
      return;
    }
    this.clearApiError();
    this.planMutation
      .mutateAsync({ userId: this.user.id, targetCompanyId })
      .then((report) => {
        this.report.set(report);
        this.targetCompanyControl.disable({ emitEvent: false });
      })
      // the dialog stays on step 1 with the server's text under the form
      .catch((error: unknown) =>
        this.presenter.present(error, {
          inline: this.showApiError,
          fallback: this.translate.instant(
            'move-user-company.toast.planning-failed',
          ),
        }),
      );
  }

  chooseNewOwner(): void {
    this.store.dispatch(
      clearSelectedPrincipals({ companyId: this.user.company_id }),
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds: [NomObjectClass.USER],
          singleSelectionActive: true,
          useObjectMap: true,
          popupType: PopupType.SELECT_NEW_OWNER,
          // The new owner must be an enabled user of the SOURCE company, not the
          // moved user - the picker would otherwise default to the current
          // user's active company.
          companyId: this.user.company_id,
          lockedUserIds: [this.user.id],
        },
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event?.data as ObjectDefinition[]),
        filter((objectDefinitions) => !!objectDefinitions?.length),
        switchMap((objectDefinitions: ObjectDefinition[]) =>
          this.utilsDataObjectApi
            .getPrincipalsByIds(objectDefinitions)
            .pipe(take(1)),
        ),
        tap((response) => {
          const principal: Principal | undefined = response?.items?.[0];
          if (principal) {
            this.newOwner.set(principal);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  move(): void {
    const report = this.report();
    if (!report || !this.canMove()) {
      return;
    }
    this.clearApiError();
    this.executeMutation
      .mutateAsync({
        userId: this.user.id,
        runId: report.run_id,
        newOwnerId: this.newOwner()?.id,
      })
      .then((outcome) => {
        this.outcome.set(outcome);
        const clean =
          outcome.report.run_status_id === UserCompanyMoveRunStatus.Executed &&
          (outcome.report.results?.failed ?? 0) === 0;
        if (clean) {
          this.close({ reason: DialogCloseReason.Save, data: outcome });
        }
      })
      // a 409 (owner invalid, run no longer pending) leaves the run pending, so the
      // dialog keeps the plan for a retry and shows the server's text
      .catch((error: unknown) =>
        this.presenter.present(error, {
          inline: this.showApiError,
          fallback: this.translate.instant(
            'move-user-company.toast.move-failed',
          ),
        }),
      );
  }

  /** After an execute that left something to read: Save when the run went through. */
  finish(): void {
    const outcome = this.outcome();
    if (outcome && this.executed()) {
      this.close({ reason: DialogCloseReason.Save, data: outcome });
    } else {
      this.close({ reason: DialogCloseReason.Cancel });
    }
  }

  cancel(): void {
    const report = this.report();
    if (report && !this.outcome()) {
      // fire-and-forget: the run expires server-side anyway if this is lost
      this.userApi
        .cancelCompanyMove(report.run_id)
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
    this.close({ reason: DialogCloseReason.Cancel });
  }
}
